import ToggleOffIcon from '@mui/icons-material/ToggleOff'
import ToggleOnIcon from '@mui/icons-material/ToggleOn'
import TuneIcon from '@mui/icons-material/Tune'
import ChipDelete from '@mui/joy/ChipDelete'
import Box from '@mui/material/Box'
import ListItem from '@mui/material/ListItem'
import {grey, lightBlue} from '@mui/material/colors'
import {IConceptFE} from 'interfaces/IConceptFE'
import * as React from 'react'
import {useState} from 'react'
import {Draggable} from 'react-beautiful-dnd'
import {useDispatch, useSelector} from 'react-redux'
import {updateRecentSavedSearch} from 'redux/actions/savedSearchActions'
import {selectSavedSearch} from 'redux/selectors/savedSearchSelector'
import {AppDispatch} from 'redux/store'
import ConceptWeightChangeDialog from '../toolbar/ConceptWeightChangeDialog'

export type DraggableListItemProps = {
  item: IConceptFE
  index: number
  size?: 'sm' | 'md' | 'lg' | undefined
}

// item is passed in but it is found within the savedSearchRedux state (redononly)
//so any changes to the item or concept needs to be dispatched as an update to the savedSearch
const DraggableConceptItem = ({item, index, size}: DraggableListItemProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const savedSearchOnlyRedux = useSelector(selectSavedSearch)
  const [draggableId] = useState(item.id || crypto.randomUUID())
  const [show, setShow] = React.useState<boolean>(false)

  const onConfirmIgnoreConcept = async (con: IConceptFE) => {
    if (savedSearchOnlyRedux?.id) {
      const concepts = savedSearchOnlyRedux.concepts?.map((x) => {
        if (x.id === con.id) {
          return {
            id: x.id,
            name: x.name,
            qid: x.qid,
            score: x.score,
            aliases: x.aliases,
            businesses: x.businesses,
            related_concepts: x.related_concepts,
            __notes: x.__notes,
            __lock: con.__lock,
            __weight: 1,
          }
        } else {
          return {
            id: x.id,
            name: x.name,
            qid: x.qid,
            score: x.score,
            aliases: x.aliases,
            businesses: x.businesses,
            related_concepts: x.related_concepts,
            __notes: x.__notes,
            __lock: x.__lock,
            __weight: x.__weight,
          }
        }
      })
      dispatch(
        updateRecentSavedSearch({
          id: savedSearchOnlyRedux.id,
          data: {concepts},
          originalSavedSearch: savedSearchOnlyRedux,
        })
      )
    }
  }

  const handleIgnoreChange = (v: 'exclude' | 'include', con: IConceptFE) => {
    const copyCn = {...con}
    copyCn.__lock = v
    onConfirmIgnoreConcept(copyCn)
  }

  const weightColor = (weight: number) => {
    if (weight < 0.5)
      return `linear-gradient(to right,  ${lightBlue[200]} 5%,${grey[100]} 10%, ${grey[100]} 100%)`
    if (weight >= 0.5 && weight < 1)
      return `linear-gradient(to right, ${lightBlue[200]} 50%, ${grey[100]} 50%)`
    if (weight === 1)
      return `linear-gradient(to right,  ${lightBlue[200]} 30%, ${lightBlue[200]} 20%, ${lightBlue[200]} 20%,${grey[100]} 90%)`
    else
      return `linear-gradient(to right,  ${lightBlue[200]} 50%, ${lightBlue[200]} 20%, ${lightBlue[200]} 50%,${grey[100]} 95%)`
  }

  function Dialog() {
    return <ConceptWeightChangeDialog show={show} setShow={setShow} concept={item} />
  }

  return (
    <>
      <Draggable draggableId={draggableId} index={index} key={draggableId}>
        {(provided: any, snapshot: any) => (
          <ListItem
            label={item.name}
            key={draggableId}
            sx={{
              mt: 0.2,
              mb: 0.2,
              color: grey[900],
              background: item.__lock !== 'exclude' ? weightColor(item.__weight) : grey[100],
              borderRadius: '2em',
              margin: 0.5,
              boxShadow:
                'rgba(0, 0, 0, 0.3) 0px 1px 2px, rgba(0, 0, 0, 0.2) 0px 3px 6px -1px, rgba(0, 0, 0, 0.1) 0px -1.8px 0px inset',
              width: 'auto',
              '& button': {
                backgroundColor: 'transparent',
                fontSize: '1.3rem',
              },
              '& button:hover': {
                backgroundColor: grey[400],
                fontSize: '1.3rem',
              },
              '& .MuiChipDelete-root': {
                color: item.__lock !== 'exclude' ? 'green' : 'grey',
                fontSize: '1.3rem',
              },
            }}
            secondaryAction={
              <Box display={'flex'} alignItems={'center'}>
                <TuneIcon onClick={() => setShow(true)} color={'primary'} fontSize={'medium'} />
                <ChipDelete
                  onClick={() =>
                    handleIgnoreChange(item.__lock === 'exclude' ? 'include' : 'exclude', item)
                  }
                  color={'primary'}
                >
                  {item.__lock !== 'exclude' ? <ToggleOnIcon /> : <ToggleOffIcon />}
                </ChipDelete>
              </Box>
            }
            size={size ?? 'lg'}
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
          >
            <Box paddingRight={'inherit'}>{item.name}</Box>
          </ListItem>
        )}
      </Draggable>
      {Dialog()}
    </>
  )
}

export default DraggableConceptItem
